import {
    BluetoothException,
    InvalidMacAddressException,
    BluetoothAuthorizationErrorException,
    BluetoothNotEnabledException,
    BluetoothPrintFailureException,
    BluetoothUnknownErrorException,
    ConnectionFailureException,
    DeviceFilteredException,
    FetchingUuidFailureException,
    InvalidActionException,
    InvalidProfileException,
    MissingArgumentsException,
    PairingCancelledException,
    PairingFailureException,
    PairingTimeoutException,
    IncompleteDataReceivedException,
    InvalidArgsFormatException,
    InterruptedException,
    UnsupportedCharsetException,
    DisconnectFailedException,
    UnsupportedGattEventException,
    BluetoothGattEventException,
} from '@services/bluetooth/BluetoothExceptions';
import {
    SeawardException,
    UnsupportedEnvironmentException,
    IllegalStateException,
    UnsupportedActionException,
    InvalidArgumentsException,
    NetworkException,
} from '@ch-types/SeawardExceptions';
import { CrossFrameUtils } from '@src/utils/CrossFrameUtils';
import { BatchCaptureNotImplementedException, CameraException } from '@services/camera/CameraExceptions';

import _ from 'lodash';

interface IExtraOptionsType {
    sensitive: boolean;
    /**
     * Overrides the cross-frame response timeout for this one call (the 60 s
     * default is wrong for associate-paced operations like batch capture).
     * Applied by the forwarding hop via the message's crossFrameTimeoutMillis.
     */
    crossFrameTimeoutMillis?: number;
}

/**
 * Allows to map names to constructors. This makes it easy to translate from serialized object
 * (think javascript Object) to an actual Constructor. This is needed to be able to send
 * serialized objects over Cross Frame calls and keep the object type.
 */
export class ATBaseClient {
    // objectName => objectConstructor
    private objectMap = {};

    /**
     * Registers all the Objects that may need to be translated.
     */
    constructor() {
        // Seaward Exceptions
        this.registerObject('SeawardException', SeawardException);
        this.registerObject('UnsupportedEnvironmentException', UnsupportedEnvironmentException);
        this.registerObject('InvalidArgumentsException', InvalidArgumentsException);
        this.registerObject('NetworkException', NetworkException);
        this.registerObject('UnsupportedActionException', UnsupportedActionException);
        this.registerObject('IllegalStateException', IllegalStateException);

        // Bluetooth Exceptions
        this.registerObject('BluetoothException', BluetoothException);
        this.registerObject('InvalidMacAddressException', InvalidMacAddressException);
        this.registerObject('BluetoothNotEnabledException', BluetoothNotEnabledException);
        this.registerObject('PairingCancelledException', PairingCancelledException);
        this.registerObject('PairingTimeoutException', PairingTimeoutException);
        this.registerObject('PairingFailureException', PairingFailureException);
        this.registerObject('DeviceFilteredException', DeviceFilteredException);
        this.registerObject('ConnectionFailureException', ConnectionFailureException);
        this.registerObject('BluetoothPrintFailureException', BluetoothPrintFailureException);
        this.registerObject('MissingArgumentsException', MissingArgumentsException);
        this.registerObject('InvalidProfileException', InvalidProfileException);
        this.registerObject('InvalidActionException', InvalidActionException);
        this.registerObject('FetchingUuidFailureException', FetchingUuidFailureException);
        this.registerObject('BluetoothUnknownErrorException', BluetoothUnknownErrorException);
        this.registerObject('BluetoothAuthorizationErrorException', BluetoothAuthorizationErrorException);
        this.registerObject('IncompleteDataReceivedException', IncompleteDataReceivedException);
        this.registerObject('InvalidArgsFormatException', InvalidArgsFormatException);
        this.registerObject('InterruptedException', InterruptedException);
        this.registerObject('UnsupportedCharsetException', UnsupportedCharsetException);
        this.registerObject('DisconnectFailedException', DisconnectFailedException);
        this.registerObject('UnsupportedGattEventException', UnsupportedGattEventException);
        this.registerObject('BluetoothGattEventException', BluetoothGattEventException);

        // Camera Exceptions (STARSHIP-134): batch capture errors cross the frame
        // from IhmAssociateToolsBase; without registration translate() would hand
        // callers raw objects and `instanceof` would quietly stop holding.
        this.registerObject('CameraException', CameraException);
        this.registerObject('BatchCaptureNotImplementedException', BatchCaptureNotImplementedException);
    }

    /**
     * Translates the passed object into its appropriate constructor (if possible). Returns the original
     * object if its constructor is not registered. Note that this method is safe to call with `undefined`
     * and `null` (it simply returns the same).
     *
     * @param {object} obj - The object to translate. This object must have the `model` property
     *  and that property must have been registered with the object's constructor for it to translate successfully.
     * @return {object} - An instance of the mapped constructor, if available, or the original object
     *  if it isn't.
     */
    public translate(obj) {
        if (!obj || !obj.model || !this.objectMap[obj.model]) {
            return obj;
        }
        const expObj = this.objectMap[obj.model];
        return expObj.prototype.deserialize(obj);
    }

    /**
     * Registers a name to a Constructor so that an object can be translated later.
     * Note that this ensures that the name hasn't been registered before with a different constructor to
     * prevent unintended overrides.
     *
     * @param {string} name - the name of the constructor.
     * @param {Constructor} Constructor - the reference to the Constructor. This constructor must implement the
     *  static method `deserialize` which should accept the object and should return an instance.
     */
    public registerObject(name: string, Constructor) {
        if (!name) {
            throw new Error('name is required');
        }

        if (!Constructor) {
            throw new Error('Constructor is required');
        }

        const dummyObj = new Constructor({ message: 'dummyMessage' });
        if (!dummyObj.deserialize || !_.isFunction(dummyObj.deserialize)) {
            throw new Error('Constructor must have a deserialize method');
        }

        if (this.objectMap[name] && this.objectMap[name] !== Constructor) {
            console.error(`Different constructor for name ${name} was registered. Current: ${this.objectMap[name]} new: ${Constructor}`);
            throw new Error(`Exception for name ${name} was already registered`);
        }

        this.objectMap[name] = Constructor;
    }

    /**
     * Wrapper around CrossFrame that provides Object translation (mainly to preserve the Exception type).
     * Makes a cross frame call for a method with a specified action and parameters.
     * Then, converts the returned object to a proper
     * instance (if available) and resolves if the call was successful and rejects if it wasn't.
     *
     * @param {string} method - the listener to call
     * @param {string} action - the action to call within the listener.
     * @param {object} parameters - object containing the parameters to execute the action.
     * @param {object} extraOptions - object containing extra options for the cross frame call
     *  Example
     *      {
     *          sensitive: {boolean} // to prevent UCI data from getting logged
     *      }
     * @return {Promise}
     */
    public async call(method: string, action: string, parameters: object, extraOptions?: IExtraOptionsType) {
        extraOptions = extraOptions || { sensitive: false };
        const sensitive = !!extraOptions.sensitive;
        try {
            const response = await CrossFrameUtils.sendMessageToOtherFrame({
                frame: window,
                message: {
                    method,
                    sensitive,
                    ...(extraOptions.crossFrameTimeoutMillis !== undefined ? { crossFrameTimeoutMillis: extraOptions.crossFrameTimeoutMillis } : {}),
                    data: {
                        action,
                        parameters,
                    },
                },
            });
            console.debug(`Successful call to ${action} response ${this.getDataForLoggingEvent(sensitive, response)}`);
            return this.translate(response);
        } catch (error) {
            console.debug(`Failed call to ${action} error ${this.getDataForLoggingEvent(sensitive, error)}`);
            return Promise.reject(this.translate(error));
        }
    }

    /**
     * Returns a redacted string if `sensitive` is set to true.
     * Otherwise it returns the data (doesn't convert it to JSON).
     *
     * @param {boolean} sensitive - true if `data` contains sensitive information that shouldn't be logged.
     * @param {any} data - data that could be sensitive.
     *
     * @return {any | string} returns the `data` if `sensitive` is false or a redacted string if it's `true`.
     */
    public getDataForLoggingEvent(sensitive, data) {
        if (sensitive) {
            return '<-- Redacted since sensitive=true -->';
        }
        return data;
    }
}
